import type * as React from 'react';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Checkbox from '@cloudscape-design/components/checkbox';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Toggle from '@cloudscape-design/components/toggle';
import { ALL_COUNTRIES_WILDCARD } from '@etiquetachef/types';
import { CONTINENT_KEYS, flagEmoji } from '@/features/countries/worldCountries';
import {
  ALL_PLAY_LOCATIONS,
  PLAY_LOCATIONS_BY_CONTINENT,
} from '@/features/countries/playStoreCountries';

type Props = {
  value: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
  disabled?: boolean;
};

type Group = { key: string; label: string; codes: readonly string[] };

const PLAY_SET = new Set(ALL_PLAY_LOCATIONS);

/**
 * Seleção dos países de um add-on: "Todos os países" (`["*"]`) ou, desligado,
 * os 225 locais da Google Play agrupados por continente, com marcar/desmarcar
 * tudo e um continente inteiro de uma vez.
 *
 * Origem: #954 — 2026-10-13 08:47 +0200.
 *
 * Por quê: o padrão é vender em todo lugar — quem exclui país é o console da
 * Google Play (owner, 2026-10-13) —, então "todos" é um interruptor que grava
 * o curinga `"*"`, não uma lista que envelhece. Desligado, a grade plana de
 * checkboxes (que servia para os 5 mercados de `Country`) ficaria ilegível com
 * 225 locais; o continente é a unidade em que o owner decide ("vender na
 * Europa"), então ele é a caixa de cima, tri-estado.
 *
 * Contrato: componente controlado — `value` é o conjunto atual, `onChange`
 * recebe um conjunto NOVO (nunca muta `value`). `value` com `"*"` = interruptor
 * ligado e lista oculta; desligar começa com os 225 marcados (ninguém perde
 * país sem ver). País de `value` fora da lista
 * da Play (legado, ou que a Google tirou) aparece no grupo "Outros" e continua
 * marcável: sem isso ele sumiria da tela e sairia do add-on no próximo salvar
 * sem ninguém ver.
 *
 * Usado por: `AddonEditorDialog` (criar e editar add-on, `/billing/addons`),
 * admin web.
 *
 * Dados: `"*"` (`ALL_COUNTRIES_WILDCARD`) ou códigos ISO alpha-2 de `PLAY_LOCATIONS_BY_CONTINENT`
 * (`features/countries/playStoreCountries.ts`); o nome exibido vem de
 * `Intl.DisplayNames` no idioma do i18n, e cai no código se o runtime não souber.
 *
 * Dicas: as seções começam recolhidas — a caixa do continente e o contador
 * "x/y" bastam para a decisão comum; abrir é para exceção de um país.
 *
 * Issues: #954.
 *
 * Histórico:
 *  - 2026-10-13 08:47 +0200 — (#954) criado: países da Play por continente.
 *  - 2026-10-13 09:27 +0200 — (#954) interruptor "Todos os países" (`["*"]`); 225 locais.
 *  - 2026-10-13 09:43 +0200 — (#954, auditoria A1) "Outros" não some ao desmarcar.
 */
export function AddonCountriesPicker({ value, onChange, disabled = false }: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();

  // Um `Intl.DisplayNames` por idioma, não um por país: são 225 nomes por render.
  const nameOf = useMemo(() => {
    let dn: Intl.DisplayNames | null = null;
    try {
      dn = new Intl.DisplayNames([i18n.language], { type: 'region' });
    } catch {
      dn = null;
    }
    return (code: string): string => {
      try {
        return dn?.of(code) ?? code;
      } catch {
        return code;
      }
    };
  }, [i18n.language]);

  // Auditoria 2026-10-13 09:43 +0200 (#954, A1): país fora da lista da Play
  // vinha só de `value`; desmarcado, saía de `value`, o grupo "Outros" sumia
  // e não havia como marcar de volta. Os países gravados ao abrir ficam
  // lembrados aqui (o picker remonta a cada abertura do diálogo). Acumula em
  // vez de capturar no 1º render: o diálogo preenche `value` num `useEffect`,
  // depois da montagem — um `useState` inicial pegaria o conjunto vazio.
  // Mutar o ref no render é idempotente (só adiciona).
  const seenOthers = useRef(new Set<string>());
  for (const c of value) {
    if (c !== ALL_COUNTRIES_WILDCARD && !PLAY_SET.has(c)) seenOthers.current.add(c);
  }
  const legacyOthers = [...seenOthers.current];
  const legacyKey = legacyOthers.join(',');

  const groups = useMemo<Group[]>(() => {
    const byName = (a: string, b: string): number => nameOf(a).localeCompare(nameOf(b), i18n.language);
    const out: Group[] = CONTINENT_KEYS.map((k) => ({
      key: k,
      label: t(`admin.countries.catalog.continents.${k}`),
      codes: [...PLAY_LOCATIONS_BY_CONTINENT[k]].sort(byName),
    }));
    const others = [
      ...new Set([...legacyOthers, ...value].filter((c) => c !== ALL_COUNTRIES_WILDCARD && !PLAY_SET.has(c))),
    ].sort(byName);
    if (others.length > 0) {
      out.push({ key: 'others', label: t('billing.addons.dialog.otherCountries'), codes: others });
    }
    return out;
    // `value` só entra pelo grupo "Outros": recalcular a cada clique é barato.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `legacyKey` representa `legacyOthers`
  }, [value, legacyKey, nameOf, t, i18n.language]);

  const total = groups.reduce((n, g) => n + g.codes.length, 0);
  const all = value.has(ALL_COUNTRIES_WILDCARD);

  function setMany(codes: readonly string[], on: boolean): void {
    const next = new Set(value);
    for (const c of codes) {
      if (on) next.add(c);
      else next.delete(c);
    }
    onChange(next);
  }

  const allToggle = (
    <Toggle
      checked={all}
      disabled={disabled}
      description={t('billing.addons.dialog.allCountriesHint')}
      onChange={({ detail }): void => {
        // Desligar começa com todos os locais marcados: sair do "*" não pode
        // tirar país de venda sem o super-admin ver (#954).
        onChange(detail.checked ? new Set([ALL_COUNTRIES_WILDCARD]) : new Set(ALL_PLAY_LOCATIONS));
      }}
    >
      {t('billing.addons.dialog.allCountriesToggle')}
    </Toggle>
  );

  if (all) return allToggle;

  return (
    <SpaceBetween size="s">
      {allToggle}
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        <Box variant="strong">
          {t('billing.addons.dialog.countriesSelected', { selected: value.size, total })}
        </Box>
        <Button
          variant="inline-link"
          disabled={disabled}
          onClick={(): void => {
            setMany(groups.flatMap((g) => g.codes), true);
          }}
        >
          {t('billing.addons.dialog.selectAll')}
        </Button>
        <Button
          variant="inline-link"
          disabled={disabled}
          onClick={(): void => {
            onChange(new Set());
          }}
        >
          {t('billing.addons.dialog.clearAll')}
        </Button>
      </SpaceBetween>

      {groups.map((g) => {
        const selected = g.codes.filter((c) => value.has(c)).length;
        const full = selected === g.codes.length;
        return (
          <ExpandableSection
            key={g.key}
            variant="default"
            headerText={g.label}
            headerActions={
              <Checkbox
                checked={full}
                indeterminate={selected > 0 && !full}
                disabled={disabled}
                ariaLabel={g.label}
                onChange={(): void => {
                  setMany(g.codes, !full);
                }}
              >
                {`${String(selected)}/${String(g.codes.length)}`}
              </Checkbox>
            }
          >
            <ColumnLayout columns={3}>
              {g.codes.map((c) => (
                <Checkbox
                  key={c}
                  checked={value.has(c)}
                  disabled={disabled}
                  onChange={(): void => {
                    setMany([c], !value.has(c));
                  }}
                >
                  {`${flagEmoji(c)} ${nameOf(c)}`}
                </Checkbox>
              ))}
            </ColumnLayout>
          </ExpandableSection>
        );
      })}
    </SpaceBetween>
  );
}
